import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface LoadingStateProps {
  stages: string[];
  tips: string[];
  subtitle?: string;
}

export default function LoadingState({
  stages,
  tips,
  subtitle,
}: LoadingStateProps) {
  const [stageIndex, setStageIndex] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setStageIndex((i) => (i + 1) % stages.length);
    }, 1400);
    return () => clearInterval(id);
  }, [stages.length]);

  useEffect(() => {
    const id = setInterval(() => {
      setTipIndex((i) => (i + 1) % tips.length);
    }, 3600);
    return () => clearInterval(id);
  }, [tips.length]);

  return (
    <div className="flex flex-col items-center text-center py-10 md:py-14">
      <Spinner />

      <div className="h-9 md:h-10 mt-8 mb-2 flex items-center">
        <AnimatePresence mode="wait">
          <motion.h2
            key={stageIndex}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="font-display font-semibold text-xl md:text-2xl text-text-primary"
          >
            {stages[stageIndex]}
            <DotsEllipsis />
          </motion.h2>
        </AnimatePresence>
      </div>

      {subtitle && (
        <p className="font-sans text-sm text-text-muted max-w-sm">{subtitle}</p>
      )}

      <div className="mt-10 w-24 h-px bg-border" />

      <div className="mt-6 min-h-[44px] max-w-sm flex items-start">
        <AnimatePresence mode="wait">
          <motion.p
            key={tipIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="font-sans text-sm text-text-secondary italic leading-relaxed"
          >
            {tips[tipIndex]}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div aria-hidden className="relative w-20 h-20 md:w-24 md:h-24">
      <svg
        viewBox="0 0 96 96"
        className="w-full h-full animate-spin"
        style={{ animationDuration: '2s', animationTimingFunction: 'linear' }}
      >
        <circle
          cx="48"
          cy="48"
          r="42"
          stroke="var(--color-border)"
          strokeWidth="4"
          fill="none"
        />
        <circle
          cx="48"
          cy="48"
          r="42"
          stroke="var(--color-primary)"
          strokeWidth="4"
          strokeLinecap="round"
          fill="none"
          style={{
            animation: 'spinner-dash 1.5s ease-in-out infinite',
            transformOrigin: 'center',
          }}
        />
      </svg>
    </div>
  );
}

function DotsEllipsis() {
  return (
    <span aria-hidden className="inline-flex ml-1">
      {[0, 0.15, 0.3].map((delay) => (
        <motion.span
          key={delay}
          className="inline-block"
          animate={{ opacity: [0.2, 1, 0.2] }}
          transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut', delay }}
        >
          .
        </motion.span>
      ))}
    </span>
  );
}
